import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { loadMapsLibrary, onAuthFailure } from '../MapPicker/googleMapsLoader';
import { stylesForTheme } from '../MapPicker/mapTheme';
import { isMapPoint, metresBetween } from '../../../utils/geo';
import styles from './PinCompareMap.module.scss';

/* ────────────────────────────────────────────────────────────
 * PinCompareMap — two (or more) labelled points on one map,
 * read-only, so "next street" and "another state" look as
 * different as they are.
 *
 *   <PinCompareMap
 *     points={[
 *       { lat: 8.5241, lng: 76.9366, label: 'S', title: 'Saved pin', tone: 'secondary' },
 *       { lat: 8.5277, lng: 76.9420, label: 'D', title: 'Delivered here', tone: 'primary' },
 *     ]}
 *     ariaLabel="Map comparing the saved pin with the delivery"
 *   />
 *
 * Each point is a filled dot with a one-letter label; the first two
 * are joined by a dashed line and the view zooms to fit them. Points
 * closer than `sameSpotMetres` are drawn as they are, but the line
 * and the zoom-to-fit are skipped — fitBounds on two identical points
 * zooms to the maximum and shows one blank tile.
 *
 * Fills the gap between the two existing maps: GeofenceMap shows ONE
 * point (Leaflet), MapPicker CHOOSES one (Google). This one compares,
 * on the same Google loader and dark cartography as MapPicker, so the
 * key, the auth-failure handling and the theme all stay in one place.
 * If the map cannot load (no key, offline, a rejected key), the box
 * says so; callers keep the coordinates in text beside it, so nothing
 * depends on the map appearing.
 * ──────────────────────────────────────────────────────────── */

// Dot colours come from tokens.scss, read when the dots are drawn:
// Google's marker icons take literal colours only (see mapTheme.js). Each
// tone's letter uses that tone's own "on" colour, so a dark letter sits on
// the amber dot and a white one on the others.
const TONE_TOKEN = {
  primary: ['--color-primary', '--color-on-primary'],
  secondary: ['--color-secondary', '--color-on-primary'],
  success: ['--color-success', '--color-on-success'],
  warning: ['--color-warning', '--color-on-warning'],
  danger: ['--color-danger', '--color-on-danger'],
  info: ['--color-info', '--color-on-info'],
};

const token = (name) => {
  try {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  } catch {
    return '';
  }
};

const PinCompareMap = ({
  points,
  sameSpotMetres,
  ariaLabel,
  loadingText,
  unavailableText,
  loadLibrary,
  className,
}) => {
  const boxRef = useRef(null);
  const mapRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | unavailable

  // Primitive values only, so a caller passing a fresh array each render
  // does not redraw the map.
  const valid = (Array.isArray(points) ? points : []).filter(isMapPoint);
  const key = valid
    .map((p) => `${p.lat},${p.lng},${p.label || ''},${p.tone || ''},${p.title || ''}`)
    .join('|');

  useEffect(() => {
    let cancelled = false;
    const drawn = [];
    const pts = valid;
    if (!pts.length) {
      setStatus('unavailable');
      return undefined;
    }
    setStatus('loading');

    const offAuth = onAuthFailure(() => {
      if (!cancelled) setStatus('unavailable');
    });

    Promise.all([loadLibrary('maps'), loadLibrary('marker'), loadLibrary('core')])
      .then(([mapsLib, markerLib, core]) => {
        if (cancelled || !boxRef.current) return;
        const map = new mapsLib.Map(boxRef.current, {
          center: { lat: pts[0].lat, lng: pts[0].lng },
          zoom: 17,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          // Inside a scrolling dialog: the page scrolls, Ctrl+wheel zooms.
          gestureHandling: 'cooperative',
          styles: stylesForTheme() || undefined,
        });
        mapRef.current = map;

        const ring = token('--color-surface') || 'white';
        pts.forEach((p) => {
          const [fill, on] = TONE_TOKEN[p.tone] || TONE_TOKEN.primary;
          drawn.push(
            new markerLib.Marker({
              map,
              position: { lat: p.lat, lng: p.lng },
              title: p.title || undefined,
              label: p.label
                ? { text: p.label, color: token(on) || 'white', fontWeight: '700', fontSize: '12px' }
                : undefined,
              icon: {
                path: core.SymbolPath.CIRCLE,
                scale: 11,
                fillColor: token(fill) || 'teal',
                fillOpacity: 1,
                strokeColor: ring,
                strokeWeight: 2,
              },
            })
          );
        });

        const [a, b] = pts;
        if (b && metresBetween(a, b) > sameSpotMetres) {
          drawn.push(
            new mapsLib.Polyline({
              map,
              path: [
                { lat: a.lat, lng: a.lng },
                { lat: b.lat, lng: b.lng },
              ],
              strokeOpacity: 0,
              icons: [
                {
                  icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, scale: 3 },
                  offset: '0',
                  repeat: '14px',
                },
              ],
            })
          );
          const bounds = new core.LatLngBounds();
          pts.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
          map.fitBounds(bounds, 56);
        }
        if (!cancelled) setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('unavailable');
      });

    return () => {
      cancelled = true;
      offAuth();
      drawn.forEach((shape) => shape.setMap && shape.setMap(null));
      mapRef.current = null;
    };
    // `key` stands for `valid` (see above); the loader is injected once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, sameSpotMetres, loadLibrary]);

  // Follow a light/dark flip while open, the way MapPicker does.
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined;
    const observer = new MutationObserver(() => {
      if (mapRef.current) mapRef.current.setOptions({ styles: stylesForTheme() });
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className={[styles.frame, className || ''].filter(Boolean).join(' ')}>
      <div ref={boxRef} className={styles.canvas} role="img" aria-label={ariaLabel} />
      {status !== 'ready' && (
        <div className={styles.overlay} data-testid="pin-compare-map-state">
          {status === 'loading' ? loadingText : unavailableText}
        </div>
      )}
    </div>
  );
};

PinCompareMap.propTypes = {
  // { lat, lng, label?, title?, tone? } — points that are not usable map
  // points (missing or non-numeric coordinates) are skipped.
  points: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
      label: PropTypes.string,
      title: PropTypes.string,
      tone: PropTypes.oneOf(Object.keys(TONE_TOKEN)),
    })
  ),
  // Closer than this the first two points count as one spot: no line, no
  // zoom-to-fit. Phone GPS is rarely better than a few metres.
  sameSpotMetres: PropTypes.number,
  ariaLabel: PropTypes.string,
  loadingText: PropTypes.string,
  unavailableText: PropTypes.string,
  // (name) => Promise<library>. Injected by tests; the app uses the
  // shared Google loader.
  loadLibrary: PropTypes.func,
  className: PropTypes.string,
};

PinCompareMap.defaultProps = {
  points: [],
  sameSpotMetres: 25,
  ariaLabel: 'Map',
  loadingText: 'Loading map…',
  unavailableText: 'Map unavailable. Compare the coordinates instead.',
  loadLibrary: loadMapsLibrary,
  className: '',
};

export default memo(PinCompareMap);
